import { useTick } from "../mcptt/hooks";
import type { GpsPing } from "../mcptt/hooks";

interface Props {
  pings:    ReadonlyArray<GpsPing>;
  open:     boolean;
  onClose:  () => void;
  onLocate: (deviceId: string) => void;
}

/**
 * Right-side drawer over the map column. Lists recent GPS pings
 * (newest first) — clicking a row pans the existing map to that pin
 * via the focus mechanism we already have.
 *
 * The drawer is always rendered when `open` so the slide-in animation
 * works; when closed we collapse the width with CSS instead of
 * unmounting (cheap, also lets the close X stay clickable during the
 * fade-out).
 */
export function GpsPingsDrawer({ pings, open, onClose, onLocate }: Props) {
  const now = useTick(1000);
  if (!open) return null;

  return (
    <div className="gps-drawer" role="dialog" aria-label="GPS pings">
      <div className="gps-drawer-head">
        <span className="gps-drawer-title">GPS PINGS</span>
        <span className="gps-drawer-meta">{pings.length}{pings.length === 30 ? "+" : ""}</span>
        <button
          type="button"
          className="gps-drawer-close"
          onClick={onClose}
          aria-label="Close GPS pings drawer"
          title="Close"
        >
          {"\u2715"}
        </button>
      </div>

      <div className="gps-drawer-body">
        {pings.length === 0 ? (
          <div className="empty">
            NO PINGS YET<br />
            Use LIVE PING in a subscriber<br />or LOC on a talkgroup tile
          </div>
        ) : (
          pings.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`gps-drawer-row${p.isFresh ? " is-fresh" : ""}`}
              onClick={() => onLocate(p.deviceId)}
              title={`Locate ${p.name} on the map`}
            >
              <div className="gps-drawer-row-line">
                <span className="gps-drawer-row-time">{formatTime(new Date(p.ts))}</span>
                <span className="gps-drawer-row-name">{p.name}</span>
                {p.isFresh ? <span className="gps-drawer-row-pulse">{"\u25CF NEW"}</span> : null}
              </div>
              <div className="gps-drawer-row-line gps-drawer-row-meta">
                <span className="t-mono">{p.lat.toFixed(5)}, {p.lon.toFixed(5)}</span>
                {p.accuracy != null ? <span className="t-mono">&plusmn;{Math.round(p.accuracy)}m</span> : null}
                <span className="t-mono" style={{ marginLeft: "auto", color: "var(--steel-600)" }}>
                  {formatAge(now - p.ts)} ago
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function formatTime(d: Date): string {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function formatAge(ms: number): string {
  if (ms < 0) return "0s";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h`;
}
